import React, { useState, useMemo, useCallback } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Chip,
  TextField,
  Button,
  IconButton,
  Divider,
  LinearProgress,
  Alert,
  AlertTitle,
  Drawer,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tooltip,
  Collapse,
  Stack,
} from '@mui/material';
import {
  TextFields as RichTextIcon,
  TableChart as TableIcon,
  BarChart as ChartIcon,
  Image as ImageIcon,
  Map as MapIcon,
  CheckCircle as MappedIcon,
  RadioButtonUnchecked as UnmappedIcon,
  ExpandMore as ExpandIcon,
  ExpandLess as CollapseIcon,
  AutoAwesome as AIIcon,
  CloudUpload as UploadIcon,
  Inventory2 as LibraryIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedFetch } from '../../../../hooks/useAuthenticatedFetch';
import {
  DeliverableAsset,
  DeliverableAssetMappingWithAsset,
  TemplateControl,
  TemplateSection,
  AssetType,
  CreateAssetRequest,
  TableContent,
  LLMGenerationRequest,
  LLMGenerationResponse,
} from '@horwath-htl/shared-types';

// ============================================================
// Props and Interfaces
// ============================================================

interface AssetsStepProps {
  deliverableId: number;
  templateId: string;
  selectedSections: string[];
  template: {
    sections_config?: { sections: TemplateSection[] } | null;
    controls_config?: { controls: TemplateControl[] } | null;
  };
  onMappingsChange: (mappings: AssetMapping[]) => void;
}

interface AssetMapping {
  controlId: string;
  assetId?: string;
  content?: any;
}

interface ControlWithMapping {
  control: TemplateControl;
  mapping?: DeliverableAssetMappingWithAsset;
  sectionName?: string;
}

// ============================================================
// Control Type Helpers
// ============================================================

const controlTypeIcons: Record<string, React.ReactNode> = {
  rich_text: <RichTextIcon />,
  table: <TableIcon />,
  chart: <ChartIcon />,
  image: <ImageIcon />,
  map: <MapIcon />,
  auto_mapped: <MappedIcon color="action" />,
};

const controlTypeLabels: Record<string, string> = {
  rich_text: 'Rich Text',
  table: 'Table',
  chart: 'Chart',
  image: 'Image',
  map: 'Map',
  auto_mapped: 'Auto-mapped',
};

// ============================================================
// Sub-Components
// ============================================================

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  placeholder?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  disabled,
  placeholder,
}) => {
  return (
    <TextField
      fullWidth
      multiline
      rows={10}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      placeholder={placeholder || 'Enter rich text content...'}
      variant="outlined"
      sx={{
        '& .MuiInputBase-root': {
          fontFamily: 'inherit',
        },
      }}
    />
  );
};

interface TableEditorProps {
  value: TableContent | null;
  onChange: (value: TableContent) => void;
  disabled?: boolean;
}

const TableEditor: React.FC<TableEditorProps> = ({ value, onChange, disabled }) => {
  const defaultTable: TableContent = {
    headers: ['Column 1', 'Column 2', 'Column 3'],
    rows: [['', '', '']],
    formatting: {
      headerStyle: 'bold',
      alignment: ['left', 'left', 'left'],
      numberFormat: ['text', 'text', 'text'],
    },
  };

  const tableData = value || defaultTable;

  const handleHeaderChange = (index: number, newValue: string) => {
    const newHeaders = [...tableData.headers];
    newHeaders[index] = newValue;
    onChange({ ...tableData, headers: newHeaders });
  };

  const handleCellChange = (rowIndex: number, colIndex: number, newValue: string) => {
    const newRows = tableData.rows.map((row, ri) =>
      ri === rowIndex
        ? row.map((cell, ci) => (ci === colIndex ? newValue : cell))
        : [...row]
    );
    onChange({ ...tableData, rows: newRows });
  };

  const addRow = () => {
    const newRow = tableData.headers.map(() => '');
    onChange({ ...tableData, rows: [...tableData.rows, newRow] });
  };

  const addColumn = () => {
    const newHeaders = [...tableData.headers, `Column ${tableData.headers.length + 1}`];
    const newRows = tableData.rows.map((row) => [...row, '']);
    const newFormatting = {
      ...tableData.formatting,
      alignment: [...tableData.formatting.alignment, 'left'],
      numberFormat: [...tableData.formatting.numberFormat, 'text'],
    };
    onChange({ ...tableData, headers: newHeaders, rows: newRows, formatting: newFormatting });
  };

  return (
    <Box>
      <Box sx={{ overflowX: 'auto', mb: 2 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {tableData.headers.map((header, index) => (
                <th key={index} style={{ padding: '8px', border: '1px solid #ccc' }}>
                  <TextField
                    size="small"
                    value={header}
                    onChange={(e) => handleHeaderChange(index, e.target.value)}
                    disabled={disabled}
                    fullWidth
                    variant="standard"
                    InputProps={{
                      sx: { fontWeight: 'bold' },
                    }}
                  />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tableData.rows.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {row.map((cell, colIndex) => (
                  <td key={colIndex} style={{ padding: '8px', border: '1px solid #ccc' }}>
                    <TextField
                      size="small"
                      value={cell}
                      onChange={(e) => handleCellChange(rowIndex, colIndex, e.target.value)}
                      disabled={disabled}
                      fullWidth
                      variant="standard"
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
      <Stack direction="row" spacing={1}>
        <Button size="small" onClick={addRow} disabled={disabled} startIcon={<AddIcon />}>
          Add Row
        </Button>
        <Button size="small" onClick={addColumn} disabled={disabled} startIcon={<AddIcon />}>
          Add Column
        </Button>
      </Stack>
    </Box>
  );
};

interface PlaceholderEditorProps {
  type: string;
  message?: string;
}

const PlaceholderEditor: React.FC<PlaceholderEditorProps> = ({ type, message }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: 200,
        border: '2px dashed',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'action.hover',
        p: 3,
      }}
    >
      {controlTypeIcons[type] || <RichTextIcon />}
      <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
        {controlTypeLabels[type]} Editor
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {message || 'Coming soon - this feature is under development'}
      </Typography>
    </Box>
  );
};

interface ImageUploaderProps {
  value: string | null;
  onChange: (blobPath: string) => void;
  disabled?: boolean;
}

const ImageUploader: React.FC<ImageUploaderProps> = ({ value, onChange, disabled }) => {
  const [preview, setPreview] = useState<string | null>(null);

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      // For MVP, we'll just store the file name
      // In production, this would upload to blob storage
      onChange(`uploads/${file.name}`);
    }
  };

  return (
    <Box>
      <input
        accept="image/*"
        style={{ display: 'none' }}
        id="image-upload-input"
        type="file"
        onChange={handleFileChange}
        disabled={disabled}
      />
      <label htmlFor="image-upload-input">
        <Button
          variant="outlined"
          component="span"
          disabled={disabled}
          startIcon={<UploadIcon />}
          fullWidth
          sx={{ mb: 2 }}
        >
          Upload Image
        </Button>
      </label>
      {(preview || value) && (
        <Box
          sx={{
            mt: 2,
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            textAlign: 'center',
          }}
        >
          {preview ? (
            <img
              src={preview}
              alt="Preview"
              style={{ maxWidth: '100%', maxHeight: 300, objectFit: 'contain' }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Image: {value}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

interface LLMGenerationDialogProps {
  open: boolean;
  onClose: () => void;
  onGenerate: (content: string) => void;
  controlName: string;
  isGenerating: boolean;
}

const LLMGenerationDialog: React.FC<LLMGenerationDialogProps> = ({
  open,
  onClose,
  onGenerate,
  controlName,
  isGenerating,
}) => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const [prompt, setPrompt] = useState('');
  const [wordCount, setWordCount] = useState<number>(250);
  const [tone, setTone] = useState<'professional' | 'formal' | 'conversational'>('professional');
  const [error, setError] = useState<string | null>(null);
  const [generatedContent, setGeneratedContent] = useState<string | null>(null);

  const handleGenerate = async () => {
    if (!prompt.trim()) {
      setError('Please enter a prompt');
      return;
    }

    setError(null);
    setGeneratedContent(null);

    try {
      const request: LLMGenerationRequest = {
        prompt: `Generate content for the "${controlName}" section of an appraisal report. ${prompt}`,
        wordCount,
        tone,
      };

      const response = await authenticatedFetch('/api/deliverables/assets/llm/generate', {
        method: 'POST',
        body: JSON.stringify(request),
        timeout: 60000, // 60 second timeout for LLM
      });

      if (!response?.ok) {
        const errorData = await response?.json();
        throw new Error(errorData?.error || 'Generation failed');
      }

      const result: LLMGenerationResponse = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Generation failed');
      }

      setGeneratedContent(result.content?.html || '');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate content');
    }
  };

  const handleUseContent = () => {
    if (generatedContent) {
      onGenerate(generatedContent);
      handleClose();
    }
  };

  const handleClose = () => {
    setPrompt('');
    setWordCount(250);
    setTone('professional');
    setError(null);
    setGeneratedContent(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Generate Content with AI</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Generate content for: <strong>{controlName}</strong>
        </Typography>

        <TextField
          fullWidth
          label="Prompt"
          placeholder="Describe what content you want to generate..."
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          multiline
          rows={3}
          sx={{ mb: 2 }}
          disabled={isGenerating}
        />

        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Target Word Count"
              type="number"
              value={wordCount}
              onChange={(e) => setWordCount(Number(e.target.value))}
              inputProps={{ min: 50, max: 5000 }}
              disabled={isGenerating}
            />
          </Grid>
          <Grid item xs={6}>
            <FormControl fullWidth>
              <InputLabel>Tone</InputLabel>
              <Select
                value={tone}
                label="Tone"
                onChange={(e) =>
                  setTone(e.target.value as 'professional' | 'formal' | 'conversational')
                }
                disabled={isGenerating}
              >
                <MenuItem value="professional">Professional</MenuItem>
                <MenuItem value="formal">Formal</MenuItem>
                <MenuItem value="conversational">Conversational</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {generatedContent && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Generated Content:
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                maxHeight: 300,
                overflow: 'auto',
                bgcolor: 'background.default',
              }}
            >
              <div dangerouslySetInnerHTML={{ __html: generatedContent }} />
            </Paper>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isGenerating}>
          Cancel
        </Button>
        {generatedContent ? (
          <>
            <Button onClick={handleGenerate} disabled={isGenerating}>
              Regenerate
            </Button>
            <Button variant="contained" onClick={handleUseContent}>
              Use This Content
            </Button>
          </>
        ) : (
          <Button
            variant="contained"
            onClick={handleGenerate}
            disabled={isGenerating || !prompt.trim()}
            startIcon={isGenerating ? <CircularProgress size={20} /> : <AIIcon />}
          >
            {isGenerating ? 'Generating...' : 'Generate'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

interface AssetLibraryDrawerProps {
  open: boolean;
  onClose: () => void;
  onSelect: (asset: DeliverableAsset) => void;
  assets: DeliverableAsset[];
  isLoading: boolean;
  filterType?: AssetType;
}

const AssetLibraryDrawer: React.FC<AssetLibraryDrawerProps> = ({
  open,
  onClose,
  onSelect,
  assets,
  isLoading,
  filterType,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState<AssetType | 'all'>(filterType || 'all');

  const filteredAssets = useMemo(() => {
    return assets.filter((asset) => {
      const matchesSearch =
        !searchQuery ||
        asset.name.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesType = typeFilter === 'all' || asset.asset_type === typeFilter;
      return matchesSearch && matchesType;
    });
  }, [assets, searchQuery, typeFilter]);

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <Box sx={{ width: 400, p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">Asset Library</Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <TextField
          fullWidth
          size="small"
          placeholder="Search assets..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{ mb: 2 }}
        />

        <FormControl fullWidth size="small" sx={{ mb: 2 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={typeFilter}
            label="Type"
            onChange={(e) => setTypeFilter(e.target.value as AssetType | 'all')}
          >
            <MenuItem value="all">All Types</MenuItem>
            <MenuItem value="rich_text">Rich Text</MenuItem>
            <MenuItem value="table">Table</MenuItem>
            <MenuItem value="chart">Chart</MenuItem>
            <MenuItem value="image">Image</MenuItem>
            <MenuItem value="map">Map</MenuItem>
          </Select>
        </FormControl>

        <Divider sx={{ mb: 2 }} />

        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredAssets.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No assets found
          </Typography>
        ) : (
          <List>
            {filteredAssets.map((asset) => (
              <ListItem key={asset.id} disablePadding>
                <ListItemButton onClick={() => onSelect(asset)}>
                  <ListItemIcon>{controlTypeIcons[asset.asset_type]}</ListItemIcon>
                  <ListItemText
                    primary={asset.name}
                    secondary={
                      <Box component="span" sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        <Chip
                          size="small"
                          label={controlTypeLabels[asset.asset_type]}
                          variant="outlined"
                        />
                        {asset.is_mapped && (
                          <Chip size="small" label="Mapped" color="success" variant="outlined" />
                        )}
                      </Box>
                    }
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        )}
      </Box>
    </Drawer>
  );
};

// ============================================================
// Main Component
// ============================================================

export const AssetsStep: React.FC<AssetsStepProps> = ({
  deliverableId,
  templateId,
  selectedSections,
  template,
  onMappingsChange,
}) => {
  const { authenticatedFetch } = useAuthenticatedFetch();
  const queryClient = useQueryClient();

  // State
  const [selectedControlId, setSelectedControlId] = useState<string | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set());
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'mapped' | 'unmapped'>('all');
  const [assetContent, setAssetContent] = useState<Record<string, any>>({});
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);
  const [isLLMDialogOpen, setIsLLMDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  // Fetch existing assets for this deliverable
  const {
    data: assetsData,
    isLoading: isLoadingAssets,
  } = useQuery({
    queryKey: ['deliverable-assets', deliverableId],
    queryFn: async () => {
      const response = await authenticatedFetch(
        `/api/deliverables/${deliverableId}/assets/list?limit=100`
      );
      if (!response?.ok) {
        throw new Error('Failed to fetch assets');
      }
      return response.json();
    },
    enabled: !!deliverableId,
  });

  // Fetch existing mappings
  const {
    data: mappingsData,
    isLoading: isLoadingMappings,
  } = useQuery({
    queryKey: ['deliverable-mappings', deliverableId, templateId],
    queryFn: async () => {
      const response = await authenticatedFetch(
        `/api/deliverables/${deliverableId}/mappings?templateId=${templateId}`
      );
      if (!response?.ok) {
        throw new Error('Failed to fetch mappings');
      }
      return response.json();
    },
    enabled: !!deliverableId && !!templateId,
  });

  // Create asset mutation
  const createAssetMutation = useMutation({
    mutationFn: async (request: CreateAssetRequest) => {
      const response = await authenticatedFetch(`/api/deliverables/${deliverableId}/assets`, {
        method: 'POST',
        body: JSON.stringify(request),
      });
      if (!response?.ok) {
        const error = await response?.json();
        throw new Error(error?.error || 'Failed to create asset');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliverable-assets', deliverableId] });
    },
  });

  // Create mapping mutation
  const createMappingMutation = useMutation({
    mutationFn: async ({ assetId, controlId, sectionId }: { assetId: string; controlId: string; sectionId?: string }) => {
      const response = await authenticatedFetch(`/api/deliverables/${deliverableId}/mappings`, {
        method: 'POST',
        body: JSON.stringify({
          templateId,
          mappings: [{ assetId, controlId, sectionId }],
        }),
      });
      if (!response?.ok) {
        const error = await response?.json();
        throw new Error(error?.error || 'Failed to create mapping');
      }
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deliverable-mappings', deliverableId, templateId] });
      queryClient.invalidateQueries({ queryKey: ['deliverable-assets', deliverableId] });
    },
  });

  // Build controls list with mapping status
  const controlsWithMappings = useMemo<ControlWithMapping[]>(() => {
    const sections = template.sections_config?.sections || [];
    const controls = template.controls_config?.controls || [];
    const mappings = mappingsData?.mappings || [];

    // Filter controls by selected sections
    const filteredControls = controls.filter((control) => {
      if (!control.section_id) return true;
      return selectedSections.includes(control.section_id);
    });

    // Add mapping info to controls
    return filteredControls.map((control) => {
      const mapping = mappings.find(
        (m: DeliverableAssetMappingWithAsset) => m.control_id === control.id
      );
      const section = sections.find((s) => s.id === control.section_id);
      return {
        control,
        mapping,
        sectionName: section?.name,
      };
    });
  }, [template, selectedSections, mappingsData]);

  // Group controls by section
  const controlsBySection = useMemo(() => {
    const grouped: Record<string, ControlWithMapping[]> = {};
    const ungrouped: ControlWithMapping[] = [];

    controlsWithMappings.forEach((item) => {
      if (item.control.section_id) {
        if (!grouped[item.control.section_id]) {
          grouped[item.control.section_id] = [];
        }
        grouped[item.control.section_id].push(item);
      } else {
        ungrouped.push(item);
      }
    });

    return { grouped, ungrouped };
  }, [controlsWithMappings]);

  // Filter controls
  const filteredControls = useMemo(() => {
    return controlsWithMappings.filter((item) => {
      const matchesType = filterType === 'all' || item.control.type === filterType;
      const matchesStatus =
        filterStatus === 'all' ||
        (filterStatus === 'mapped' && item.mapping) ||
        (filterStatus === 'unmapped' && !item.mapping);
      return matchesType && matchesStatus;
    });
  }, [controlsWithMappings, filterType, filterStatus]);

  // Calculate progress
  const mappedCount = controlsWithMappings.filter((c) => c.mapping).length;
  const totalCount = controlsWithMappings.length;
  const progressPercent = totalCount > 0 ? (mappedCount / totalCount) * 100 : 0;

  // Get selected control info
  const selectedControl = controlsWithMappings.find(
    (c) => c.control.id === selectedControlId
  );

  // Toggle section expansion
  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) {
        next.delete(sectionId);
      } else {
        next.add(sectionId);
      }
      return next;
    });
  };

  // Handle saving asset and creating mapping
  const handleSaveAsset = useCallback(async () => {
    if (!selectedControl) return;

    const content = assetContent[selectedControl.control.id];
    if (!content) {
      return;
    }

    try {
      // Create new asset
      const assetRequest: CreateAssetRequest = {
        name: `${selectedControl.control.name} - Content`,
        assetType: selectedControl.control.type as AssetType,
        content,
        metadata: {
          source: 'manual',
        },
      };

      const assetResult = await createAssetMutation.mutateAsync(assetRequest);

      // Create mapping
      await createMappingMutation.mutateAsync({
        assetId: assetResult.asset.id,
        controlId: selectedControl.control.id,
        sectionId: selectedControl.control.section_id,
      });

      // Update parent component
      const newMappings = controlsWithMappings.map((c) => ({
        controlId: c.control.id,
        assetId: c.control.id === selectedControl.control.id ? assetResult.asset.id : c.mapping?.asset_id,
      }));
      onMappingsChange(newMappings);
    } catch (error) {
      console.error('Error saving asset:', error);
    }
  }, [selectedControl, assetContent, createAssetMutation, createMappingMutation, controlsWithMappings, onMappingsChange]);

  // Handle selecting an existing asset from library
  const handleSelectAsset = async (asset: DeliverableAsset) => {
    if (!selectedControl) return;

    try {
      await createMappingMutation.mutateAsync({
        assetId: asset.id,
        controlId: selectedControl.control.id,
        sectionId: selectedControl.control.section_id,
      });

      setIsLibraryOpen(false);

      // Update parent
      const newMappings = controlsWithMappings.map((c) => ({
        controlId: c.control.id,
        assetId: c.control.id === selectedControl.control.id ? asset.id : c.mapping?.asset_id,
      }));
      onMappingsChange(newMappings);
    } catch (error) {
      console.error('Error mapping asset:', error);
    }
  };

  // Handle LLM generated content
  const handleLLMGenerate = (content: string) => {
    if (!selectedControl) return;

    setAssetContent((prev) => ({
      ...prev,
      [selectedControl.control.id]: content,
    }));
  };

  // Render control list item
  const renderControlItem = (item: ControlWithMapping) => {
    const isSelected = selectedControlId === item.control.id;
    const isMapped = !!item.mapping;

    return (
      <ListItem key={item.control.id} disablePadding>
        <ListItemButton
          selected={isSelected}
          onClick={() => setSelectedControlId(item.control.id)}
          sx={{
            borderLeft: 3,
            borderColor: isMapped ? 'success.main' : 'warning.main',
          }}
        >
          <ListItemIcon sx={{ minWidth: 40 }}>
            {controlTypeIcons[item.control.type] || <RichTextIcon />}
          </ListItemIcon>
          <ListItemText
            primary={item.control.name}
            secondary={
              <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Typography variant="caption" color="text.secondary">
                  {controlTypeLabels[item.control.type]}
                </Typography>
                {item.control.required && (
                  <Chip size="small" label="Required" color="error" variant="outlined" sx={{ height: 18 }} />
                )}
              </Box>
            }
          />
          <Tooltip title={isMapped ? 'Mapped' : 'Unmapped'}>
            {isMapped ? (
              <MappedIcon color="success" fontSize="small" />
            ) : (
              <UnmappedIcon color="action" fontSize="small" />
            )}
          </Tooltip>
        </ListItemButton>
      </ListItem>
    );
  };

  // Render editor based on control type
  const renderEditor = () => {
    if (!selectedControl) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: 'text.secondary',
          }}
        >
          <Typography variant="h6">Select a control</Typography>
          <Typography variant="body2">
            Choose a control from the list to add or edit content
          </Typography>
        </Box>
      );
    }

    const { control, mapping } = selectedControl;
    const currentContent = assetContent[control.id] || mapping?.asset?.content || '';

    // Auto-mapped controls
    if (control.type === 'auto_mapped' || control.auto_mapped) {
      return (
        <Alert severity="info">
          <AlertTitle>Auto-Mapped Control</AlertTitle>
          This control is automatically populated from {control.data_source || 'system data'}.
          No manual content entry is needed.
        </Alert>
      );
    }

    return (
      <Box>
        {/* Control header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6">{control.name}</Typography>
          {control.description && (
            <Typography variant="body2" color="text.secondary">
              {control.description}
            </Typography>
          )}
          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
            <Chip
              size="small"
              icon={controlTypeIcons[control.type] as React.ReactElement}
              label={controlTypeLabels[control.type]}
            />
            {control.required && <Chip size="small" label="Required" color="error" />}
            {mapping && (
              <Chip
                size="small"
                label={`Mapped to: ${mapping.asset.name}`}
                color="success"
                variant="outlined"
              />
            )}
          </Box>
        </Box>

        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<LibraryIcon />}
            onClick={() => setIsLibraryOpen(true)}
          >
            Use Existing Asset
          </Button>
          {control.type === 'rich_text' && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<AIIcon />}
              onClick={() => setIsLLMDialogOpen(true)}
            >
              Generate with AI
            </Button>
          )}
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Editor */}
        {control.type === 'rich_text' && (
          <RichTextEditor
            value={currentContent}
            onChange={(value) =>
              setAssetContent((prev) => ({ ...prev, [control.id]: value }))
            }
            disabled={createAssetMutation.isPending || createMappingMutation.isPending}
          />
        )}

        {control.type === 'table' && (
          <TableEditor
            value={currentContent ? JSON.parse(currentContent) : null}
            onChange={(value) =>
              setAssetContent((prev) => ({ ...prev, [control.id]: JSON.stringify(value) }))
            }
            disabled={createAssetMutation.isPending || createMappingMutation.isPending}
          />
        )}

        {control.type === 'image' && (
          <ImageUploader
            value={mapping?.asset?.blob_path || null}
            onChange={(blobPath) =>
              setAssetContent((prev) => ({ ...prev, [control.id]: blobPath }))
            }
            disabled={createAssetMutation.isPending || createMappingMutation.isPending}
          />
        )}

        {control.type === 'chart' && (
          <PlaceholderEditor type="chart" message="Chart configuration coming soon" />
        )}

        {control.type === 'map' && (
          <PlaceholderEditor type="map" message="Map configuration coming soon" />
        )}

        {/* Save button */}
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            onClick={handleSaveAsset}
            disabled={
              !assetContent[control.id] ||
              createAssetMutation.isPending ||
              createMappingMutation.isPending
            }
            startIcon={
              createAssetMutation.isPending || createMappingMutation.isPending ? (
                <CircularProgress size={20} />
              ) : null
            }
          >
            {mapping ? 'Update Mapping' : 'Save & Map'}
          </Button>
        </Box>
      </Box>
    );
  };

  // Loading state
  if (isLoadingAssets || isLoadingMappings) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  // No controls
  if (controlsWithMappings.length === 0) {
    return (
      <Alert severity="info">
        <AlertTitle>No Controls Found</AlertTitle>
        The selected template sections do not contain any content controls.
        Please go back and select different sections, or choose a different template.
      </Alert>
    );
  }

  return (
    <Box sx={{ height: '100%' }}>
      {/* Progress indicator */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
          <Typography variant="subtitle2">
            Mapping Progress
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {mappedCount} of {totalCount} controls mapped
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progressPercent}
          sx={{
            height: 8,
            borderRadius: 4,
            bgcolor: 'action.hover',
            '& .MuiLinearProgress-bar': {
              borderRadius: 4,
              bgcolor: progressPercent === 100 ? 'success.main' : 'primary.main',
            },
          }}
        />
      </Paper>

      {/* Validation summary for unmapped required controls */}
      {controlsWithMappings.some((c) => c.control.required && !c.mapping) && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Required Controls Unmapped</AlertTitle>
          {controlsWithMappings
            .filter((c) => c.control.required && !c.mapping)
            .map((c) => c.control.name)
            .join(', ')}
        </Alert>
      )}

      {/* Main content - two panel layout */}
      <Grid container spacing={2} sx={{ height: 'calc(100% - 100px)' }}>
        {/* Left panel - Control list */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {/* Filters */}
            <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                <FilterIcon fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                Filter Controls
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Type</InputLabel>
                    <Select
                      value={filterType}
                      label="Type"
                      onChange={(e) => setFilterType(e.target.value)}
                    >
                      <MenuItem value="all">All Types</MenuItem>
                      <MenuItem value="rich_text">Rich Text</MenuItem>
                      <MenuItem value="table">Table</MenuItem>
                      <MenuItem value="chart">Chart</MenuItem>
                      <MenuItem value="image">Image</MenuItem>
                      <MenuItem value="map">Map</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status</InputLabel>
                    <Select
                      value={filterStatus}
                      label="Status"
                      onChange={(e) => setFilterStatus(e.target.value as 'all' | 'mapped' | 'unmapped')}
                    >
                      <MenuItem value="all">All</MenuItem>
                      <MenuItem value="mapped">Mapped</MenuItem>
                      <MenuItem value="unmapped">Unmapped</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Box>

            {/* Control list */}
            <Box sx={{ flex: 1, overflow: 'auto' }}>
              <List dense>
                {/* Ungrouped controls */}
                {controlsBySection.ungrouped
                  .filter((item) => filteredControls.includes(item))
                  .map(renderControlItem)}

                {/* Grouped by section */}
                {Object.entries(controlsBySection.grouped).map(([sectionId, items]) => {
                  const visibleItems = items.filter((item) => filteredControls.includes(item));
                  if (visibleItems.length === 0) return null;

                  const section = template.sections_config?.sections?.find((s) => s.id === sectionId);
                  const isExpanded = expandedSections.has(sectionId);

                  return (
                    <React.Fragment key={sectionId}>
                      <ListItem
                        disablePadding
                        sx={{ bgcolor: 'action.hover' }}
                      >
                        <ListItemButton onClick={() => toggleSection(sectionId)}>
                          <ListItemText
                            primary={
                              <Typography variant="subtitle2">
                                {section?.name || sectionId}
                              </Typography>
                            }
                            secondary={`${visibleItems.filter((i) => i.mapping).length}/${visibleItems.length} mapped`}
                          />
                          {isExpanded ? <CollapseIcon /> : <ExpandIcon />}
                        </ListItemButton>
                      </ListItem>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding dense>
                          {visibleItems.map(renderControlItem)}
                        </List>
                      </Collapse>
                    </React.Fragment>
                  );
                })}
              </List>
            </Box>
          </Paper>
        </Grid>

        {/* Right panel - Asset editor */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ height: '100%', p: 3, overflow: 'auto' }}>
            {renderEditor()}
          </Paper>
        </Grid>
      </Grid>

      {/* Asset Library Drawer */}
      <AssetLibraryDrawer
        open={isLibraryOpen}
        onClose={() => setIsLibraryOpen(false)}
        onSelect={handleSelectAsset}
        assets={assetsData?.assets || []}
        isLoading={isLoadingAssets}
        filterType={selectedControl?.control.type as AssetType}
      />

      {/* LLM Generation Dialog */}
      {selectedControl && (
        <LLMGenerationDialog
          open={isLLMDialogOpen}
          onClose={() => setIsLLMDialogOpen(false)}
          onGenerate={handleLLMGenerate}
          controlName={selectedControl.control.name}
          isGenerating={isGenerating}
        />
      )}
    </Box>
  );
};

export default AssetsStep;
